import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Plus, FileText, ArrowRight, Trash2, Edit, Building, ChevronLeft } from 'lucide-react';
import { useAlert } from '../../context/alert/AlertProvider';
import { useTranslation } from 'react-i18next';
import EditScenarioModal from '../../components/forms/scenario/EditScenarioModal';
import ConfirmDeleteModal from '../../components/forms/scenario/ConfirmDeleteModal';

const ManageScenariosPage = () => {
  const [scenarios, setScenarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingScenario, setEditingScenario] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [scenarioToDelete, setScenarioToDelete] = useState(null);

  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const fetchScenarios = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/scenario', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch scenarios');
      }

      const data = await response.json();
      setScenarios(data);
    } catch (error) {
      console.error('Error fetching scenarios:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.fetchScenarios'),
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [showAlert, t]);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const handleCreateScenario = async (scenarioData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://www.ia-schedule.com:4139/api/scenario', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scenarioData)
      });

      if (!response.ok) {
        throw new Error('Failed to create scenario');
      }

      const newScenario = await response.json();
      setScenarios([...scenarios, newScenario]);
      setShowCreateModal(false);

      showAlert({
        title: t('alert.success'),
        description: t('scenarios.created'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error creating scenario:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.createScenario'),
        variant: 'destructive'
      });
    }
  };

  const handleEditScenario = (scenario) => {
    setEditingScenario(scenario);
    setShowCreateModal(true);
  };

  const handleUpdateScenario = async (scenarioData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/scenario/${editingScenario.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scenarioData)
      });

      if (!response.ok) {
        throw new Error('Failed to update scenario');
      }

      setScenarios(scenarios.map(s =>
        s.id === editingScenario.id
          ? { ...s, ...scenarioData }
          : s
      ));

      setShowCreateModal(false);
      setEditingScenario(null);

      showAlert({
        title: t('alert.success'),
        description: t('scenarios.updated'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error updating scenario:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.updateScenario'),
        variant: 'destructive'
      });
    }
  };

  const handleDeleteClick = (scenario) => {
    setScenarioToDelete(scenario);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://www.ia-schedule.com:4139/api/scenario/${scenarioToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete scenario');
      }

      setScenarios(scenarios.filter(s => s.id !== scenarioToDelete.id));
      setShowDeleteModal(false);
      setScenarioToDelete(null);

      showAlert({
        title: t('alert.success'),
        description: t('scenarios.deleted'),
        variant: 'success'
      });
    } catch (error) {
      console.error('Error deleting scenario:', error);
      showAlert({
        title: t('alert.error'),
        description: t('alert.error.deleteScenario'),
        variant: 'destructive'
      });
    }
  };

  const handleViewScenarioDetails = (scenario) => {
    navigate(`/myscenarios`, {
      state: {
        scenarioId: scenario.id,
        selectedScenarioId: scenario.id
      }
    });
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gray-100 -mt-16 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => window.history.back()}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-800"
            >
              <ChevronLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('scenarios.management.title')}</h1>
              <p className="text-sm text-gray-500 mt-1">{t('scenarios.management.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={() => {
              setEditingScenario(null);
              setShowCreateModal(true);
            }}
            className="px-4 py-2 rounded-lg flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700"
          >
            <Plus size={18} />
            {t('scenarios.create')}
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : scenarios.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-900 mb-2">{t('scenarios.empty.title')}</h3>
              <p className="text-gray-600 mb-6">{t('scenarios.empty.description')}</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 inline-flex items-center gap-2"
              >
                <Plus size={18} />
                {t('scenarios.create')}
              </button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scenarios.map(scenario => (
              <Card key={scenario.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col h-full">
                    <div className="mb-4">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <Building className="h-5 w-5 text-indigo-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900">{scenario.name}</h3>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditScenario(scenario);
                            }}
                            className="p-1 text-gray-400 hover:text-indigo-600 rounded-full hover:bg-gray-100"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(scenario);
                            }}
                            className="p-1 text-gray-400 hover:text-red-600 rounded-full hover:bg-gray-100"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {scenario.description && (
                        <p className="text-gray-600 text-sm mt-2 ml-10">{scenario.description}</p>
                      )}
                    </div>

                    <div className="mt-auto pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                          {t('scenarios.management.created')}: {new Date(scenario.createdAt).toLocaleDateString()}
                        </div>
                        <button
                          onClick={() => handleViewScenarioDetails(scenario)}
                          className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                        >
                          {t('scenarios.management.viewDetails')}
                          <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <EditScenarioModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingScenario(null);
        }}
        onSave={editingScenario ? handleUpdateScenario : handleCreateScenario}
        scenario={editingScenario}
      />

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('scenarios.delete.title')}
        message={t('scenarios.delete.message', { name: scenarioToDelete?.name })}
      />
    </div>
  );
};

export default ManageScenariosPage;
